import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  setPersistence,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User
} from 'firebase/auth';
import type { UserModel } from '../Models/UserModel';

/**
 * Firebase, kept exactly as the JavaScript application has it.
 *
 * Tutors already have accounts, so the project, the provider and the
 * persistence model all stay: a rebuild is not a reason to make everyone sign
 * up again. The API verifies these same tokens against Google's certificates,
 * with no Admin SDK on either side.
 *
 * The configuration below is publishable by design. A Firebase web config
 * identifies the project; it does not authorise anything, which is why every
 * API route verifies the token rather than trusting the caller.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyC61wypvlPcabtsCL4Sw_1GOqwJqJ2u07U',
  authDomain: 'handsynth2.firebaseapp.com',
  projectId: 'handsynth2',
  storageBucket: 'handsynth2.firebasestorage.app',
  messagingSenderId: '533169606964',
  appId: '1:533169606964:web:acd63fbe89ebcec2719561'
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let provider: GoogleAuthProvider | null = null;
let ready: Promise<Auth> | null = null;

/**
 * One initialisation shared by every caller. It also drains any pending
 * redirect result, so an error from a redirect sign-in surfaces instead of
 * leaving the user on a blank login screen wondering what happened.
 */
function ensureAuth(): Promise<Auth> {
  if (ready) return ready;
  ready = (async () => {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch {
      // A browser with storage blocked still works, one session at a time.
    }
    try {
      await getRedirectResult(auth);
    } catch (error) {
      console.error('[auth] redirect result', error);
    }
    return auth;
  })();
  return ready;
}

/**
 * The native account chooser (Android's Credential Manager / Google Sign-In,
 * iOS's native flow) followed by handing its credential to the JS SDK via
 * `signInWithCredential`. That second step is what makes the native app and
 * the web app the same Firebase session as far as the rest of this file is
 * concerned: `getIdToken` and `onChange` both read the JS SDK only and never
 * need to know a native sign-in happened at all.
 */
async function signInWithGoogleNative(instance: Auth): Promise<UserModel | null> {
  try {
    const result = await FirebaseAuthentication.signInWithGoogle();
    const idToken = result.credential?.idToken;
    // No ID token with no thrown error is the native chooser being dismissed:
    // the tutor changed their mind, the same as a closed popup on web.
    if (!idToken) return null;
    const credential = GoogleAuthProvider.credential(idToken, result.credential?.accessToken);
    const jsResult = await signInWithCredential(instance, credential);
    return toUserModel(jsResult.user);
  } catch (error) {
    const message = String((error as { message?: string }).message ?? '');
    // Android's Credential Manager and iOS's native chooser both report a
    // cancelled sign-in as a rejected promise rather than a null result, so
    // unlike the web popup flow this has to be pattern-matched on the message.
    if (/cancel/i.test(message)) return null;
    throw error;
  }
}

function toUserModel(user: User): UserModel {
  return {
    uid: user.uid,
    email: user.email,
    name: user.displayName,
    emailVerified: user.emailVerified
  };
}

export const AuthService = {
  /**
   * Subscribes to sign-in state. The callback fires once with the restored
   * session before anything else, which is what lets the shell tell "signed
   * out" apart from "not known yet" and stop flashing the login screen on
   * every reload.
   */
  onChange(listener: (user: UserModel | null) => void): () => void {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    void ensureAuth().then(instance => {
      if (cancelled) return;
      unsubscribe = onAuthStateChanged(instance, user => {
        listener(user ? toUserModel(user) : null);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },

  /**
   * A fresh ID token for the next API call. Firebase caches and refreshes it,
   * so asking every time is cheap and an expired token never leaves here.
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    const instance = await ensureAuth();
    const user = instance.currentUser;
    return user ? user.getIdToken(forceRefresh) : null;
  },

  /**
   * A popup, falling back to a redirect only when the popup itself could not
   * open. Blocked third-party cookies break the redirect too, so those errors
   * are surfaced rather than swallowed into a flow that would also fail.
   *
   * Inside the native app this is a different flow entirely: Google has
   * blocked OAuth sign-in inside embedded WebViews since 2016 (an "unsafe
   * browser" error, not a Magbit bug), so `signInWithPopup`/`signInWithRedirect`
   * cannot work there at all. `signInWithGoogleNative` uses the platform's own
   * account chooser instead and hands the result to the JS SDK below it, so
   * everything downstream of this function (`getIdToken`, `onChange`) stays
   * the same on every platform.
   */
  async signInWithGoogle(): Promise<UserModel | null> {
    const instance = await ensureAuth();
    if (Capacitor.isNativePlatform()) {
      return signInWithGoogleNative(instance);
    }
    try {
      const result = await signInWithPopup(instance, provider!);
      return toUserModel(result.user);
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(instance, provider!);
        return null;
      }
      // The user closing the popup is a decision, not a failure.
      if (code === 'auth/cancelled-popup-request' || code === 'auth/popup-closed-by-user') {
        return null;
      }
      throw error;
    }
  },

  async signOut(): Promise<void> {
    const instance = await ensureAuth();
    if (Capacitor.isNativePlatform()) {
      // Clears the native account chooser's cached session too, or the next
      // sign-in silently returns the same account with no chooser shown.
      await FirebaseAuthentication.signOut();
    }
    await signOut(instance);
  }
};
